/* ==========================================================================
   LV — mobile toolbar
   assets/lv-toolbar.js

   Two jobs:
     1. Hide the bar on scroll-down, show it on scroll-up.
     2. Mark the active item, in case Liquid could not (see below).

   Everything else is CSS. The bar works with JS disabled — it simply stays
   visible, which is the correct fallback.
   ========================================================================== */
(() => {
  'use strict';

  if (customElements.get('lv-toolbar')) return;

  const REDUCED = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Below this many pixels of movement, nothing happens. Without a threshold
     the bar flickers on the rubber-band bounce at the top and bottom of iOS
     scrolling. */
  const THRESHOLD = 8;

  /* Never hide the bar within this distance of the top of the page. */
  const TOP_ZONE = 60;

  class LVToolbar extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.lv-tb');
      if (!this.bar) return;

      this.lastY = window.scrollY;
      this.ticking = false;

      this.markActive();
      this.bindMenuFallback();
      this.bindSheets();

      if (this.dataset.hideOnScroll === 'true' && !REDUCED()) {
        this._onScroll = () => this.onScroll();
        // passive: this listener never calls preventDefault, and saying so
        // lets the browser scroll without waiting for it.
        window.addEventListener('scroll', this._onScroll, { passive: true });
      }

      /* The cart drawer and the search modal both sit above the toolbar. If
         the toolbar stayed put it would float over the open drawer. */
      this._onKey = (e) => {
        if (e.key === 'Escape') this.show();
      };
      document.addEventListener('keydown', this._onKey);
    }

    disconnectedCallback() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onKey) document.removeEventListener('keydown', this._onKey);

      /* A sheet left open when the theme editor re-renders the section would
         leave the page permanently unscrollable, and the chat bubble hidden
         with no way to bring it back. */
      document.body.style.overflow = '';
      document.documentElement.classList.remove('lv-sheet-open');
      (this._sheets || []).forEach(({ el }) => {
        if (!el) return;
        if (el._onKey) document.removeEventListener('keydown', el._onKey);
        if (el._drag) {
          window.removeEventListener('pointermove', el._drag.onMove);
          window.removeEventListener('pointerup', el._drag.onUp);
          window.removeEventListener('pointercancel', el._drag.onUp);
        }
      });
    }

    /* ---- bottom sheet ------------------------------------------------------
       The sheets are siblings of this element, not children, so they are
       looked up in the document by the id on the button.
       ---------------------------------------------------------------------- */
    bindSheets() {
      const openers = [...this.querySelectorAll('[data-sheet-open]')];
      if (!openers.length) return;

      openers.forEach((btn) => {
        const sheet = document.getElementById(btn.dataset.sheetOpen);
        if (!sheet) return;

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openSheet(sheet, btn);
        });

        sheet.querySelectorAll('[data-sheet-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeSheet(sheet, btn));
        });

        this.bindDrag(sheet, btn);

        // Escape closes, but only the sheet that is actually open.
        sheet._onKey = (e) => {
          if (e.key === 'Escape') this.closeSheet(sheet, btn);
        };
      });

      this._sheets = openers
        .map((b) => ({ btn: b, el: document.getElementById(b.dataset.sheetOpen) }))
        .filter((x) => x.el);
    }

    /* ---- drag to dismiss ---------------------------------------------------
       Pointer events cover touch, mouse and pen in one path.

       Two rules make this behave rather than fight the sheet's own scrolling:

       1. A drag may only START on the grab handle or the header. If a drag
          could start anywhere, scrolling a long menu would drag the sheet
          shut instead.
       2. Downward drags only. Pulling up past the top does nothing, because
          a sheet that can be dragged above its resting position just leaves a
          gap under it.
       ---------------------------------------------------------------------- */
    bindDrag(sheet, btn) {
      const panel = sheet.querySelector('.lv-sheet__panel');
      const grab = sheet.querySelector('.lv-sheet__grab');
      const head = sheet.querySelector('.lv-sheet__head');
      if (!panel) return;

      let startY = 0;
      let dy = 0;
      let dragging = false;
      let height = 0;

      const from = (e) => {
        // The close button lives in the header; a tap on it is not a drag.
        if (e.target.closest('[data-sheet-close]')) return false;
        return (grab && grab.contains(e.target)) || (head && head.contains(e.target));
      };

      const onDown = (e) => {
        if (!from(e)) return;
        dragging = true;
        startY = e.clientY;
        dy = 0;
        height = panel.getBoundingClientRect().height || 1;
        sheet.classList.add('is-dragging');
      };

      const onMove = (e) => {
        if (!dragging) return;
        // Clamped at 0: upward movement is ignored, not applied as negative.
        dy = Math.max(0, e.clientY - startY);
        panel.style.transform = `translate3d(0, ${dy}px, 0)`;
        // The scrim fades in step with the drag so the gesture feels connected.
        const scrim = sheet.querySelector('.lv-sheet__scrim');
        if (scrim) scrim.style.opacity = String(Math.max(0, 1 - dy / height));
      };

      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        sheet.classList.remove('is-dragging');

        // Inline styles must go, or they would beat the class-based transform
        // and the sheet could never animate closed or reopen correctly.
        panel.style.transform = '';
        const scrim = sheet.querySelector('.lv-sheet__scrim');
        if (scrim) scrim.style.opacity = '';

        // Past a quarter of its height, or a decisive flick, it closes.
        if (dy > height * 0.25 || dy > 120) {
          this.closeSheet(sheet, btn);
        }
        dy = 0;
      };

      panel.addEventListener('pointerdown', onDown);
      // window, so the gesture survives the finger leaving the panel.
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);

      // Kept so disconnectedCallback can remove them; window listeners are
      // never cleaned up automatically and would leak on every re-render.
      sheet._drag = { onMove, onUp };
    }

    openSheet(sheet, btn) {
      sheet.hidden = false;

      /* Two frames, not one. Removing [hidden] and adding .is-open in the same
         frame means the browser only ever paints the final state, and the
         transition never runs. The first rAF lets it paint the closed state;
         the second starts the animation from there. */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => sheet.classList.add('is-open'));
      });

      btn.setAttribute('aria-expanded', 'true');

      // Stop the page behind the sheet from scrolling with it.
      this._scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';

      /* Hides the Shopify Inbox bubble and the scroll-to-top button for as
         long as the sheet is up. Raising the sheet's z-index alone is not
         enough — the bubble would still float on top of the scrim, over the
         menu links. See the THIRD-PARTY WIDGETS block in lv-toolbar.css. */
      document.documentElement.classList.add('lv-sheet-open');

      document.addEventListener('keydown', sheet._onKey);

      // Move focus in so a keyboard or screen reader user lands inside.
      const first = sheet.querySelector('.lv-sheet__close');
      if (first) first.focus({ preventScroll: true });
    }

    closeSheet(sheet, btn) {
      sheet.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.documentElement.classList.remove('lv-sheet-open');
      document.removeEventListener('keydown', sheet._onKey);

      /* Wait for the slide-down to finish before hiding, or the panel vanishes
         instantly. transitionend can fail to fire if the element is hidden
         another way first, so a timer backs it up. */
      const done = () => { sheet.hidden = true; };
      let fired = false;
      const once = () => { if (!fired) { fired = true; done(); } };
      sheet.addEventListener('transitionend', once, { once: true });
      setTimeout(once, 450);

      btn.focus({ preventScroll: true });
    }

    /* ---- menu fallback -----------------------------------------------------
       A trigger element only works if the theme's own script has registered a
       drawer under that exact name. If the name is wrong — or the header
       registers its menu some other way entirely — the trigger renders fine
       and does absolutely nothing when tapped, with no error.

       Rather than guess the name, this finds the button the HEADER already
       uses to open its menu and clicks that. Whatever the header does, the
       toolbar now does the same thing.
       ---------------------------------------------------------------------- */
    bindMenuFallback() {
      const items = [...this.querySelectorAll('[data-menu-trigger]')];
      if (!items.length) return;

      items.forEach((el) => {
        el.addEventListener('click', (e) => {
          const opener = this.findHeaderMenuButton();
          if (!opener) return;          // nothing found: leave the trigger alone
          e.preventDefault();
          e.stopPropagation();
          opener.click();
        });
      });
    }

    /* Look for the header's menu opener, most specific selector first.
       Everything here is a generic pattern, not a guess at this theme's
       private class names — the first one that exists and is not inside the
       toolbar itself wins. */
    findHeaderMenuButton() {
      const SELECTORS = [
        'header xo-drawer-trigger[xo-name*="menu"]',
        'header xo-modal-trigger[xo-name*="menu"]',
        'xo-drawer-trigger[xo-name*="menu"]',
        'xo-modal-trigger[xo-name*="menu"]',
        'header [class*="menu-toggle"]',
        'header [class*="hamburger"]',
        'header [class*="__burger"]',
        'header button[aria-label*="menu" i]',
        'header a[aria-label*="menu" i]',
        '[class*="header"] [class*="menu-icon"]',
        'button[aria-controls*="menu"]',
        'button[aria-label*="menu" i]'
      ];

      for (const sel of SELECTORS) {
        const found = [...document.querySelectorAll(sel)]
          // never click ourselves — that would recurse forever
          .filter((el) => !el.closest('lv-toolbar'))
          // skip anything hidden: a theme often ships both a mobile and a
          // desktop opener and only one is live at a time
          .filter((el) => el.offsetParent !== null || el.getClientRects().length);
        if (found.length) return found[0];
      }
      return null;
    }

    /* ---- scroll ----------------------------------------------------------- */
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      // rAF, not the scroll event directly: scroll fires far more often than
      // the screen repaints, and writing a style on every one causes jank.
      requestAnimationFrame(() => {
        const y = Math.max(0, window.scrollY);
        const dy = y - this.lastY;

        if (Math.abs(dy) > THRESHOLD) {
          if (y < TOP_ZONE) {
            this.show();
          } else if (dy > 0) {
            this.hide();
          } else {
            this.show();
          }
          this.lastY = y;
        }
        this.ticking = false;
      });
    }

    hide() { this.dataset.hidden = 'true'; }
    show() { this.dataset.hidden = 'false'; }

    /* ---- active item ------------------------------------------------------
       Liquid already sets is-active by comparing request.path. That misses
       two cases it cannot see:

         - a link written with the full domain rather than a path
         - a link with a query string or trailing slash

       Both are common in a merchant-entered URL field, so the comparison is
       redone here against the browser's own resolved href.
       ---------------------------------------------------------------------- */
    markActive() {
      const items = [...this.querySelectorAll('a.lv-tb__item')];
      if (!items.length) return;
      if (this.querySelector('.lv-tb__item.is-active')) return;  // Liquid won

      const here = location.pathname.replace(/\/+$/, '') || '/';

      let best = null;
      let bestLen = -1;

      items.forEach((a) => {
        // a.pathname resolves relative links and strips the query for us.
        const path = (a.pathname || '').replace(/\/+$/, '') || '/';

        // Exact match always wins.
        if (path === here) {
          best = a;
          bestLen = Infinity;
          return;
        }
        // Otherwise the longest matching prefix wins, so /collections/rings
        // beats /collections on a rings page. Home is excluded: every path
        // starts with '/', so it would match everything.
        if (path !== '/' && here.startsWith(path + '/') && path.length > bestLen) {
          best = a;
          bestLen = path.length;
        }
      });

      if (best) {
        best.classList.add('is-active');
        best.setAttribute('aria-current', 'page');
      }
    }
  }

  customElements.define('lv-toolbar', LVToolbar);
})();
